import {
  Anchor,
  Arrow,
  Content,
  PopoverContentProps,
  PopoverProps,
  PopoverTriggerProps,
  Portal,
  Root,
  Trigger,
} from "@radix-ui/react-popover";
import classNames from "classnames";
import * as React from "react";
import DeviceHelper from "../../../helpers/DeviceHelper";
import { Overlay } from "../Overlay";
import styles from "./Popover.styles.scss";

export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof Content>,
  PopoverContentProps
>(({ children, className, ...props }, ref) => {
  const { setOpen, touchTimer } = usePopover();

  return (
    <Content
      className={classNames(
        styles.PopoverContent,
        className,
        "rounded-sm bg-black px-4 py-3 text-sm text-white",
      )}
      ref={ref}
      {...props}
      onOpenAutoFocus={(e: Event) => {
        // デフォルトの挙動だとContent要素に自動でフォーカスがあたるが、
        // FSだとツールチップ的な使い方をすることが多いためこの挙動を無効化している
        e.preventDefault();
      }}
      onInteractOutside={() => {
        // タッチデバイスで画面の他の場所をタップした際にPopoverを閉じる
        if (touchTimer.current) {
          clearTimeout(touchTimer.current);
        }
        setOpen(false);
      }}
    >
      {children}
      <Arrow className="fill-black" />
    </Content>
  );
});

// FSの要件に合わせて開閉状態の制御を上書きするため、開閉状態を管理するContextを定義
// 下層コンポーネントからusePopover()というhooksで参照できるようにしている
type PopoverContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  touchTimer: React.MutableRefObject<NodeJS.Timeout | null>;
};
const PopoverContext = React.createContext<PopoverContextValue | undefined>(
  undefined,
);
export const PopoverProvider = ({
  hasOverlay = false,
  children,
}: React.PropsWithChildren<{ hasOverlay?: boolean }>) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const touchTimer = React.useRef<NodeJS.Timeout | null>(null);

  const closePopover = (e: React.MouseEvent<HTMLDivElement>) => {
    if (touchTimer.current) {
      clearTimeout(touchTimer.current);
    }
    setOpen(false);
    e.stopPropagation();
  };

  return (
    <>
      {open && hasOverlay && (
        <Overlay onClick={closePopover} className="bg-white bg-opacity-0" />
      )}
      <PopoverContext.Provider value={{ open, setOpen, touchTimer }}>
        {children}
      </PopoverContext.Provider>
    </>
  );
};
export const usePopover = () => {
  const result = React.useContext(PopoverContext);
  if (!result) {
    throw new Error("Wrap your element with <PopoverProvider> to use Popover");
  }
  return result;
};

// Popoverはそのままだとhover時にコンテンツを表示しないが、
// FSではツールチップのような使い方も兼ねているため、hover(onMouseEnter)でも発火するようにしている
const usePopoverTriggerHandlers = () => {
  const { setOpen, touchTimer } = usePopover();

  return {
    onClick: () => {
      setOpen(true);
    },
    onTouchStart: () => {
      touchTimer.current = setTimeout(() => {
        // タップの長押しで表示させる
        setOpen(true);
      }, 700); // タップの長押しで表示させる(slackのリアクションの長押しと同程度をイメージした値)
    },
    onMouseEnter: () => {
      if (!DeviceHelper.isTouchDevice()) {
        // タブレットでは表示させない(タップの長押しで表示させるので)
        setOpen(true);
      }
    },
    onMouseLeave: () => {
      if (touchTimer.current) {
        clearTimeout(touchTimer.current);
      }
      if (!DeviceHelper.isTouchDevice()) {
        setOpen(false);
      }
    },
  };
};

export const PopoverTrigger = ({ children, ...props }: PopoverTriggerProps) => {
  const handlers = usePopoverTriggerHandlers();

  return (
    <Trigger {...props} {...handlers}>
      {children}
    </Trigger>
  );
};

type LazyPopoverProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  content: React.ReactNode;
};

// 一覧で大量に並べる Popover 用。PopoverProvider の内側で使う。
// Radix の PopoverTrigger は内部の PopperAnchor がマウント時に必ず setState を 1 回起こすため、
// 数百〜数千個が同時にマウントされると更新が連鎖し、React の更新回数の上限を超えて
// 「Maximum update depth exceeded」で落ちることがある（Sentry: BORON-WEB-2H2）。
// そこで初めて開くまでは Radix をマウントせずただの button だけを描画し、
// 開いたときに button を仮想アンカーにして Popover をマウントする
export const LazyPopover = ({ content, ...buttonProps }: LazyPopoverProps) => {
  const { open } = usePopover();
  const handlers = usePopoverTriggerHandlers();
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [hasOpened, setHasOpened] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setHasOpened(true);
    }
  }, [open]);

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        {...buttonProps}
        {...handlers}
        ref={triggerRef}
      />
      {(open || hasOpened) && (
        <Popover>
          {/* Anchor は button の描画後にしかマウントされないため、ref は必ず要素を指している */}
          <Anchor
            virtualRef={triggerRef as React.RefObject<HTMLButtonElement>}
          />
          <PopoverPortal>
            <PopoverContent>{content}</PopoverContent>
          </PopoverPortal>
        </Popover>
      )}
    </>
  );
};

export const Popover = ({ children, ...props }: PopoverProps) => {
  const { open, setOpen } = usePopover();

  return (
    <Root {...props} open={open} onOpenChange={setOpen}>
      {children}
    </Root>
  );
};

// NOTE: 以下は現状@radix-uiそのままだが、今後挙動を調整したりスタイルを変えたいことがあるかもしれないので
// general/Popoverのコンポーネントとして公開している
export const PopoverPortal = Portal;
